import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { findBookable, OFFLINE_SLUGS } from "../data/consultants";
import { OfflineConsultant } from "../components/booking/OfflineConsultant";
import { ConsultantPicker } from "../components/booking/ConsultantPicker";
import { SlotPicker } from "../components/booking/SlotPicker";
import { BookingForm } from "../components/booking/BookingForm";
import { ReasonPicker, REASONS } from "../components/booking/ReasonPicker";
import { planVisit, TESTS } from "../data/fees";
import { HolterDays } from "../components/booking/HolterDays";
import { FormPrivacyNote } from "../components/FormPrivacyNote";

const VisitChoice = ({ value, onChange }) => (
  <fieldset data-testid="followup-visit-choice" className="bg-white border border-bronze-light/30 rounded-md p-6">
    <legend className="sr-only">In person or by telephone</legend>
    <h2 className="font-serif text-2xl">How would you like your follow-up?</h2>
    <div className="mt-4 grid sm:grid-cols-2 gap-3">
      {[["follow_up", "In person at the clinic", "30 minutes · tests can be added"], ["follow_up_phone", "By telephone", "30 minutes · your consultant calls you"]].map(([k, t, d]) => (
        <button key={k} type="button" onClick={() => onChange(k)} aria-pressed={value === k} data-testid={`followup-visit-${k}`}
          className={`text-left p-4 rounded-md border transition-colors ${value === k ? "border-bronze-dark bg-cream-100 ring-1 ring-bronze-dark" : "border-stone-200 hover:border-bronze-light"}`}>
          <span className="block font-medium">{t}</span><span className="block text-xs text-ink-muted mt-0.5">{d}</span>
        </button>
      ))}
    </div>
  </fieldset>
);

const Card = ({ testId, children }) => (
  <div data-testid={testId} className="p-6 sm:p-8 bg-white border border-bronze-light/40 rounded-md max-w-2xl">{children}</div>
);

const SetupLinkForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/follow-up-access", { email });
      setSent(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  if (sent) return <p data-testid="followup-setup-sent" className="mt-4 text-sm text-ink">{sent}</p>;
  return (
    <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2" data-testid="followup-setup-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="The email you booked with"
        aria-label="The email you booked with" autoComplete="email"
        data-testid="followup-setup-email" className="flex-1 rounded-sm border border-stone-300 bg-white px-3 py-2.5 text-sm" />
      <button type="submit" disabled={busy} data-testid="followup-setup-submit" className="btn-secondary">{busy ? "Sending…" : "Email me a set-up link"}</button>
      <FormPrivacyNote className="w-full" />
    </form>
  );
};

const LoggedOut = () => (
  <Card testId="followup-logged-out">
    <h2 className="font-serif text-2xl">Log in to book your follow-up</h2>
    <p className="mt-2 text-sm text-ink-muted">Follow-up consultations are only for patients the clinic has already seen, and are booked from your secure patient account.</p>
    <Link to={`/login?next=${encodeURIComponent(`/follow-up${window.location.search}`)}`} data-testid="followup-login-link" className="btn-primary mt-5 inline-flex">Log in</Link>
    <div className="mt-8 pt-6 border-t border-stone-200/60">
      <div className="text-sm font-medium">No account yet?</div>
      <p className="text-sm text-ink-muted">Enter the email address you booked with. If we&apos;ve seen you, we&apos;ll email you a link to set one up.</p>
      <SetupLinkForm />
    </div>
  </Card>
);

const VerifyEmail = ({ email }) => {
  const [sent, setSent] = useState(false);
  const send = () => api.post("/auth/follow-up-access", { email }).then(() => setSent(true)).catch(() => toast.error("Couldn't send the email - please try again."));
  return (
    <Card testId="followup-verify-email">
      <h2 className="font-serif text-2xl">Confirm your email address</h2>
      <p className="mt-2 text-sm text-ink-muted">To protect your records, we need to check this account&apos;s email belongs to you before follow-ups can be booked. We&apos;ll email <b>{email}</b> a link to confirm it and choose a password.</p>
      {sent ? <p data-testid="followup-verify-sent" className="mt-5 text-sm text-ink">Check your inbox for the link. After using it, log in again and come back here.</p>
        : <button type="button" onClick={send} data-testid="followup-verify-btn" className="btn-primary mt-5">Email me the link</button>}
    </Card>
  );
};

const NotEligible = ({ mfa }) => (
  <Card testId="followup-not-eligible">
    <h2 className="font-serif text-2xl">{mfa === false ? "Finish setting up your account" : "Follow-ups aren't available yet"}</h2>
    <p className="mt-2 text-sm text-ink-muted">
      {mfa === false ? "Please set up 2-step login first, then come back to book your follow-up."
        : "Follow-up consultations are for patients the clinic has already seen. Once your visit is recorded you'll be able to book here. If you think this is wrong, please contact us."}
    </p>
    <div className="mt-5 flex gap-3">
      {mfa === false ? <Link to="/mfa-setup" className="btn-primary">Set up 2-step login</Link> : <Link to="/enquiry" className="btn-primary">Book a new consultation</Link>}
      <Link to="/contact" className="btn-secondary">Contact us</Link>
    </div>
  </Card>
);

const FollowUpFlow = ({ prefill }) => {
  const [pick, setPick] = useState(findBookable(prefill.consultant_slug) ? prefill.consultant_slug : "any");
  const [reasons, setReasons] = useState(() => REASONS.filter((r) => prefill.reasons.includes(r.title)).map((r) => r.slug));
  const [slot, setSlot] = useState(null);
  const [visit, setVisit] = useState(() => (new URLSearchParams(window.location.search).get("visit") === "phone" ? "follow_up_phone" : "follow_up"));
  const [refreshKey, setRefreshKey] = useState(0);
  const choose = (slug) => { setPick(slug); setSlot(null); };
  const phone = visit === "follow_up_phone";
  const shown = phone ? reasons.filter((r) => !TESTS[r]) : reasons;
  const [holterDays, setHolterDays] = useState(1);
  const plan = planVisit(shown, visit, holterDays);
  const chooseVisit = (v) => { setVisit(v); setSlot(null); };
  const offline = OFFLINE_SLUGS.includes(pick) ? findBookable(pick) : null;
  return (
    <div className="space-y-6 max-w-4xl" data-testid="followup-flow">
      <ConsultantPicker value={pick} onChange={choose} step={1} />
      {offline ? <OfflineConsultant consultant={offline} /> : <>
      <VisitChoice value={visit} onChange={chooseVisit} />
      <ReasonPicker value={shown} onChange={(r) => { setReasons(r); setSlot(null); }} exclude={phone ? Object.keys(TESTS) : []} step={2} />
      <HolterDays reasons={shown} value={holterDays} onChange={setHolterDays} />
      {shown.length > 0 && <SlotPicker value={slot} onChange={setSlot} refreshKey={refreshKey} holterDays={holterDays} consultant={pick} initialStart={prefill.suggested_date}
        tests={plan.tests} visit={plan.lookupKey} />}
      {slot && shown.length > 0 && (
        <BookingForm slot={slot} consultantSlug={pick === "any" ? "" : pick} reasons={shown} plan={plan} followUpOnly
          prefill={prefill} onSlotTaken={() => { setSlot(null); setRefreshKey((k) => k + 1); }} />
      )}
      </>}
    </div>
  );
};

const useFollowUp = (user) => {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/me/follow-up").then(({ data }) => setState(data)).catch(() => setState({ eligible: false }));
  }, [user]);
  return state;
};

const Body = ({ user, info }) => {
  if (user === null) return null;
  if (!user) return <LoggedOut />;
  if (!info) return <p className="text-sm text-ink-muted">Checking your account…</p>;
  if (info.eligible && !info.email_verified) return <VerifyEmail email={user.email} />;
  if (!info.eligible || !info.mfa_enabled) return <NotEligible mfa={info.eligible ? info.mfa_enabled : undefined} />;
  return <FollowUpFlow prefill={info.prefill} />;
};

export default function FollowUpBooking() {
  const { user } = useAuth();
  const info = useFollowUp(user);
  return (
    <div data-testid="followup-page">
      <SEO title="Book a Follow-up" description="Returning patients can book a 30-minute follow-up consultation from their secure patient account." path="/follow-up" />
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-14 max-w-5xl">
          <div className="eyebrow flex items-center gap-2"><ShieldCheck size={16} weight="duotone" /> Returning patients</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">Book a <em className="text-bronze-dark">follow-up.</em></h1>
          <p className="mt-5 text-ink-muted text-lg max-w-3xl">A 30-minute follow-up, in person or by telephone. Tests can be added to an in-person follow-up. The full price is shown on the secure payment page before you pay.</p>
        </div>
      </section>
      <section className="container-ccc py-12 max-w-5xl"><Body user={user} info={info} /></section>
    </div>
  );
}
